import { useEffect, useId, useRef } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    // Confirmação de ação destrutiva (remoção) usa o botão vermelho por padrão.
    danger?: boolean;
    isBusy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

// Diálogo de confirmação leve e com darkmode, usado antes de ações destrutivas no recovery
// (remover andamento, parte, título). Padrão do módulo: toda remoção passa por aqui — a
// exclusão é física e a auditoria registra quem/o quê, mas a confirmação evita o clique
// acidental. Ver PROGRESS.md ("Padrão de confirmação de remoção").
const ConfirmDialog = ({
    open,
    title,
    message,
    confirmLabel = 'Remover',
    cancelLabel = 'Cancelar',
    danger = true,
    isBusy = false,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) => {
    // useId chamado antes do early return (regra dos hooks). Ids únicos por instância — há
    // mais de um ConfirmDialog na página (andamento, partes, títulos), então id fixo colidiria.
    const titleId = useId();
    const messageId = useId();
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // Foco no Cancelar ao abrir (o foco entra no modal, e o default é a ação segura) e fecha
    // no Escape — mesmo padrão do SidePanelDrawer. Escape respeita isBusy, como o backdrop.
    useEffect(() => {
        if (!open) return;
        cancelButtonRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isBusy) onCancel();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, isBusy, onCancel]);

    if (!open) return null;

    const confirmClass = danger
        ? 'bg-red-600 hover:bg-red-700'
        : 'bg-blue-600 hover:bg-blue-700';

    // Enquanto a remoção está em andamento, o clique no backdrop é ignorado — mesmo
    // comportamento do botão Cancelar desabilitado. Sem isso, dava para fechar o diálogo no
    // meio da requisição e deixar a UI num estado inconsistente.
    const handleBackdropClick = () => {
        if (isBusy) return;
        onCancel();
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleBackdropClick} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={messageId}
                    className="w-full max-w-sm rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-4 space-y-4"
                >
                    <div className="flex items-start gap-3">
                        {danger && (
                            <span className="flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400">
                                <FiAlertTriangle size={20} />
                            </span>
                        )}
                        <div>
                            <h3 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
                            <p id={messageId} className="text-sm text-gray-600 dark:text-gray-300 mt-1">{message}</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            ref={cancelButtonRef}
                            onClick={onCancel}
                            disabled={isBusy}
                            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isBusy}
                            className={`px-3 py-1.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${confirmClass}`}
                        >
                            {isBusy ? 'Removendo...' : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConfirmDialog;
